/**
 * Pure helpers for the analytics context. No posthog-js, no server-only: the
 * server (member_app_visits writer) and the client (PostHog super properties)
 * derive platform and entry source from the same functions.
 */
import type {
  AppEnv,
  AppPlatform,
  EntrySource,
  MemberRole,
  NavDestination,
} from '@/lib/analytics/events'

const UUID_SEGMENT = /\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi

/**
 * Path → route template, so events never carry a member's id or an org's slug:
 * `/in/sgc/profile/<uuid>` → `/in/[org]/profile/[id]`,
 * `/in/sgc/jobs/designer-3f2a` → `/in/[org]/jobs/[slug]`.
 */
export function routeTemplate(pathname: string): string {
  return pathname
    .replace(UUID_SEGMENT, '/[id]')
    .replace(/^\/in\/[^/]+/, '/in/[org]')
    .replace(/\/(jobs|events|chapters|p|opportunities)\/(?!\[)[^/]+/g, '/$1/[slug]')
}

/** Org slug from an org-scoped path (`/in/<slug>/...`), else null. */
export function orgSlugFromPath(pathname: string): string | null {
  const match = pathname.match(/^\/in\/([^/?#]+)/)
  return match ? decodeURIComponent(match[1]) : null
}

/**
 * Platform from the User-Agent. The Capacitor shell appends "Capacitor"
 * (capacitor.config.ts → appendUserAgent), which is the only reliable native
 * signal on the server and at client boot, before the JS bridge is injected.
 */
export function appPlatformFromUserAgent(ua: string): AppPlatform {
  if (!/Capacitor/i.test(ua)) return 'web'
  if (/android/i.test(ua)) return 'android'
  if (/iphone|ipad|ipod/i.test(ua)) return 'ios'
  return 'web'
}

const KNOWN_SOURCES: readonly EntrySource[] = ['digest', 'push', 'email']

/**
 * Entry source from a query string (`?utm_source=digest&...`). No utm_source →
 * 'direct'; a value we don't send ourselves → 'other' (never stored verbatim).
 */
export function entrySourceFromSearch(search: string): EntrySource {
  let raw: string | null
  try {
    raw = new URLSearchParams(search).get('utm_source')
  } catch {
    return 'direct'
  }
  if (!raw) return 'direct'
  const value = raw.trim().toLowerCase()
  return (KNOWN_SOURCES as readonly string[]).includes(value) ? (value as EntrySource) : 'other'
}

export function appEnvFrom(value: string | undefined): AppEnv {
  return value === 'production' || value === 'staging' ? value : 'development'
}

/**
 * The viewer's role in the org, most privileged first. Staff mirrors
 * lib/utils/member-segment.ts (is_staff OR org_role = 'staff').
 */
export function memberRoleFrom(input: {
  isSuperAdmin?: boolean | null
  isAdmin?: boolean | null
  isStaff?: boolean | null
  orgRole?: string | null
}): MemberRole {
  if (input.isSuperAdmin) return 'super_admin'
  if (input.isAdmin || input.orgRole === 'admin') return 'admin'
  if (input.isStaff || input.orgRole === 'staff') return 'staff'
  return 'member'
}

/**
 * Classic dashboard tab value → nav destination. `directory` is People,
 * `community` is the Explore tab. Unknown values → null (not tracked).
 */
export function navDestinationForTab(tab: string): NavDestination | null {
  switch (tab) {
    case 'home':
      return 'home'
    case 'directory':
      return 'people'
    case 'community':
      return 'explore'
    case 'messages':
      return 'messages'
    default:
      return null
  }
}
